import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@components/primitives';

import { api } from '@services/apiService';
import { avatar } from '@utils/generateAvatar';

import css from './styles.module.css';

export interface UserAvatarProps {
  className?: string;
  uid: number;
  fallback: string;
}

export function UserAvatar({ uid, className, fallback }: UserAvatarProps) {
  const query = useQuery({
    queryKey: ['users', 'avatars', uid],
    queryFn: async () => {
      const response = await api.get(`users/avatar/${uid}`, {
        credentials: 'omit',
      });
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    enabled: uid !== 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
    // A 404 here just means this user has no custom avatar uploaded (see
    // UserService.getUserAvatar in the API) — expected, and already handled
    // below via the generated fallback avatar, so don't report it to Datadog.
    meta: { expectedErrorStatuses: [404] },
  });

  const src = useMemo(() => {
    if (query.data) {
      return query.data;
    } else if (query.isError || uid === 0) {
      return avatar(fallback);
    } else {
      return null;
    }
  }, [query.data, query.isError, uid, fallback]);

  return (
    <div className={cn(css.avatar, className)}>
      <AnimatePresence initial={false}>
        {src && (
          <motion.img
            key={src}
            src={src}
            alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
